import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BrowserSentryClientOptions } from '@micro-sentry/browser';
import { BrowserOptions, EventHint, ErrorEvent } from '@sentry/browser';
import { Observable, map } from 'rxjs';
import { ResolutionService } from './resolution.service';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import { PlatformInformationService } from '../../../../projects/platform-manager/src/public_api';

export enum Browser {
  Safari = 'Safari',
  Edge = 'Edge',
  Opera = 'Opera',
  Chrome = 'Chrome',
  Firefox = 'Firefox',
  Other = 'Other'
}

export interface sentryTracingApiResponse {
  sampling: Array<{
    browser: string;
    platform: string;
    percentage: number;
    time_range: { start: string; end: string };
  }>;
}

@Injectable({
  providedIn: 'root'
})
export class SentryService {
  private initalizedSentry = false;
  private initalizeSentryTracing = false;
  private client: { report(error: Error): void; setUser(user: object): void };
  private dsn = 'https://17c1c7e77f7c4810acbd03b3a328c3a2@o1142596.ingest.sentry.io/6325313';

  constructor(
    private platformService: PlatformInformationService,
    private httpClient: HttpClient,
    private resolutionService: ResolutionService,
    private cookieService: SsrCookieService
  ) {}

  /**
   * loads and initializes a minimalistic error reporter for sentry. Used in a custom global angular Errorhandler
   * to track issues when they arise. Will track IP and timestamps considered GDPR sensitive data when Sentry backend
   * isn't configured to anonymize ips.
   */
  async initSentry(): Promise<{ report(error: Error): void }> {
    if (!this.initalizedSentry) {
      this.initalizedSentry = true;
      const browserMicroSentryClient = await this.loadMicroSentryClient();

      this.client = new browserMicroSentryClient({
        dsn: this.dsn
      });
      this.client.setUser({ id: this.cookieService.get('artrid') });
    }
    return this.client;
  }

  isBrowser(): boolean {
    return this.platformService.getIsBrowser();
  }

  getBeforeSendHook(): (event: ErrorEvent, hint: EventHint) => ErrorEvent | null {
    return (event: ErrorEvent, hint?: EventHint): ErrorEvent | null => {
      const originalException = hint?.originalException;

      const message =
        typeof originalException === 'string'
          ? originalException
          : originalException instanceof Error
            ? originalException.message
            : '';

      if (message.toLowerCase().includes('scarab')) {
        console.error('[Sentry beforeSend] Muted SCARAB error:', message);
        return null;
      }

      return event;
    };
  }

  /**
   * loads and initializes heavy, resource intesive, intrusive, GDPR sensitive
   * Sentry client that tracks EVERYTHING while making phones run hot. Load with caution.
   * ePrivacy Directive (EU Cookie Law) -> requires opt-in user consent (not opt-out, no default on setting)
   * https://en.wikipedia.org/wiki/General_Data_Protection_Regulation#EU_Digital_Single_Market
   * https://docs.sentry.io/platforms/dotnet/configuration/options/#send-default-pii
   */
  async initSentryTracing() {
    if (!this.initalizeSentryTracing) {
      this.initalizeSentryTracing = true;
      this.sentryTracingElligibleForClient().subscribe(async res => {
        if (res) {
          const Sentry = await this.loadSentryTracingClient(); // await import('@sentry/browser');
          Sentry.init({
            dsn: this.dsn,
            beforeSend: this.getBeforeSendHook(),
            integrations: [
              Sentry.browserTracingIntegration(),
              Sentry.replayIntegration({
                maskAllText: false
              })
            ],
            denyUrls: [/cdn\.scarabresearch\.com/i, /scarab-v\d+\.js/i, /scarabresearch\.com/i],
            ignoreErrors: [/scarab/i],
            // settings required for replay and tracing Integration
            tracesSampleRate: 0.01, // 1.0 = 100%, 0.01 = 1%
            tracePropagationTargets: [/alpinresorts\.com/, /alpy\.com/], // /^https:\/\/dev\.alpy\.com/ <- example for only https
            replaysSessionSampleRate: 0.1,
            replaysOnErrorSampleRate: 1.0 // since we want to debug, replays for errors are of higher interest
          });
          Sentry.setUser({
            id: this.cookieService.get('artrid')
          });
        }
      });
    }
  }

  private getNowInMinutes(): number {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  }

  private sentryTracingElligibleForClient(): Observable<boolean> {
    // in browser group
    const browser = this.getUserAgent(navigator?.userAgent);

    // in deviceType
    const deviceType = this.resolutionService.isMobileDevice() ? 'mobile' : 'desktop';

    // in percentage
    const fortuna = Math.random() * 100;

    // in timeframe
    const nowMinutes = this.getNowInMinutes();

    return this.httpClient
      .get<sentryTracingApiResponse>(this.platformService.getApiEndpointFor('getSentryTracingConfig'))
      .pipe(
        map(res => {
          // if user was already selected, keep tracing up
          if (this.cookieService.get('sentry_tracing')) {
            return true;
          }
          const sampler = res.sampling.find(s => {
            return (
              (s.browser === browser || s.browser === Browser.Other) && // in browser group
              (s.platform === deviceType || s.platform === 'any') && // in deviceType group
              fortuna <= s.percentage && // in tracing percentage
              this.isInTime(s.time_range, nowMinutes)
            );
          });
          if (sampler) {
            // user selected for tracing, making sure to keep it that way for the next hour
            const now = new Date();
            this.cookieService.set('sentry_tracing', '1', now.setHours(now.getHours() + 1), '/');
            return true;
          }
          return false;
        })
      );
  }

  private isInTime(time_range: { start: string; end: string }, nowMinutes: number): boolean {
    const [startH, startM] = time_range.start.split(':').map(Number);
    const [endH, endM] = time_range.end.split(':').map(Number);
    const startMinutes = startH * 60 + startM; // 02:00 -> 120 minutes
    const endMinutes = endH * 60 + endM; // 14:30 -> 870 minutes

    return startMinutes <= nowMinutes && nowMinutes <= endMinutes;
  }

  /**
   *
   * @param ua navigator.userAgent or other string formatted like that
   * @returns Browser Enum or Browser Other if not applicable
   */
  private getUserAgent(ua: string): Browser {
    if (/Edg\//.test(ua)) return Browser.Edge;
    if (/OPR\//.test(ua) || /Opera/.test(ua)) return Browser.Opera;
    if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) return Browser.Chrome;
    if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return Browser.Safari;
    if (/Firefox\//.test(ua)) return Browser.Firefox;
    return Browser.Other;
  }

  // Extracted to a separate method for easier mocking in tests
  async loadMicroSentryClient(): Promise<
    new (args: BrowserSentryClientOptions) => { report(error: Error): void; setUser(user: object): void }
  > {
    const module = await import('@micro-sentry/browser');
    return module.BrowserMicroSentryClient;
  }

  async loadSentryTracingClient(): Promise<{
    init(args: BrowserOptions): void;
    browserTracingIntegration();
    replayIntegration(options?: object);
    setUser(user: object): void;
  }> {
    const Sentry = await import('@sentry/browser');
    return Sentry;
  }
}
